import { Component, ViewChild } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { UserComponent } from 'components/layout/user/user.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CredentialRequired } from 'services/credential.service';
import { LogEvent, LoggingService } from 'services/logging.service';
import { ComponentUrl } from 'services/navigation.service';
import { UserService } from 'services/user.service';
import { LogChangesComponent } from './log-changes.component';
import { DateTimeInputComponent } from 'components/input/date-time-input/date-time-input.component';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Pipes } from 'modules/pipes';
import { LogRequestModel } from 'models/generated/Logging.Api.Models.LogRequest';

@Component({
	selector: 'view-log',
	templateUrl: './view-log.component.html',
	imports: [Layout, Inputs, DateTimeInputComponent, ListView, UserComponent, LogChangesComponent, Pipes]
})
@ComponentUrl('system-logs')
@CredentialRequired(CredentialType.SystemLogAccessFull)
export class ListLogComponent {

	@ViewChild('searchButton') searchButton!: ButtonComponent;

	readonly InputType = InputType;

	logEntries?: LogEvent[];
	filteredLogEntries?: LogEvent[];
	relatedScopeLogEntries: LogEvent[] = [];
	fromDate: { date: Date, dateOnly: boolean; };
	toDate: { date: Date, dateOnly: boolean; };
	hideUnchanged = true;

	private _selectedLog?: LogEvent;
	set selectedLog(value: LogEvent | undefined) {
		this._selectedLog = value;
		this.updateScopeListview();
	}
	get selectedLog() { return this._selectedLog; }

	get previousValue() {
		return this.selectedLog?.previousValue;
	}
	get currentValue() {
		return this.selectedLog?.currentValue;
	}

	users: SelectListItemModel[];
	namespaces?: SelectListItemModel[];
	filteredEntityTypes?: SelectListItemModel[];
	private allEntityTypes?: SelectListItemModel[];

	selectedEntityId?: string;
	selectedUserId?: string;
	private _selectedNamespace?: string;
	private _selectedEntityType?: string;

	public set selectedNamespace(namespace: string | undefined) {
		this._selectedNamespace = namespace;
		this.filteredEntityTypes = this.allEntityTypes?.filter(t => t.value.toString().startsWith(namespace ? `${namespace}.` : ''));
		if (this.selectedEntityType && this.filteredEntityTypes && this.filteredEntityTypes.findIndex(t => t.value == this.selectedEntityType) < 0)
			this.selectedEntityType = undefined; // If selected type wasn't in the selected namespace, unset the type
	}
	public get selectedNamespace() { return this._selectedNamespace; }

	public set selectedEntityType(type: string | undefined) {
		this._selectedEntityType = type;
		if (type) this.selectedNamespace = type.split('.')[0];
	}
	public get selectedEntityType() { return this._selectedEntityType; }

	constructor(userService: UserService, private readonly loggingService: LoggingService) {

		// Default values: From -2 hours to +1 hour
		const fromDate = new Date(new Date().getTime() - 2 * 60 * 60 * 1000);
		const toDate = new Date(new Date().getTime() + 1 * 60 * 60 * 1000);

		this.fromDate = { date: fromDate, dateOnly: false };
		this.toDate = { date: toDate, dateOnly: false };

		this.users = userService.getUserSelectList(true, true);

		loggingService.getLoggedEntityTypes().then(types => {
			let namespaces = types.map(t => t.split('.')[0]);
			namespaces = namespaces.filter((n, i) => namespaces.indexOf(n) === i); // Get only distinct values
			this.namespaces = namespaces.map(n => new SelectListItemModel(n, n));
			this.filteredEntityTypes = this.allEntityTypes = types.map(t => new SelectListItemModel(t, t.split('.')[1]));
		});
	}

	getDate(dateString?: string | Date) {
		return dateString ? new Date(dateString) : new Date();
	}

	async updateLogEntries() {
		const entityIds = this.selectedEntityId ? [this.selectedEntityId] : [];
		let entityType = this.selectedEntityType;
		if (!entityType && this.selectedNamespace) entityType = `${this.selectedNamespace}.`;

		this.logEntries = await this.loggingService.getLogs({ from: this.fromDate.date, to: this.toDate.date, entityIds, entityType, userId: this.selectedUserId } as LogRequestModel);
		this.filterLogEntries();
		this.searchButton?.reset();
	}

	filterLogEntries() {
		this.filteredLogEntries = this.logEntries;
		if (this.filteredLogEntries && this.filteredLogEntries.length > 1000) this.filteredLogEntries = this.filteredLogEntries.slice(0, 1000);
	}

	private updateScopeListview() {
		this.relatedScopeLogEntries = [];

		if (this.selectedLog?.activityId) {
			this.relatedScopeLogEntries = this.logEntries?.filter(entry => entry.activityId === this.selectedLog?.activityId) ?? [];
		}
	}

}
